import React, { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Truck,
  FileText,
  Receipt,
  Wallet,
  Settings,
  LogOut,
  Building2,
  ChevronDown,
  Plus,
  ImageIcon,
  Loader2,
  Upload,
  Moon,
  Sun,
  Shield,
  Link2,
  RefreshCw,
  TrendingUp,
  Sliders,
  Menu,
  X
} from 'lucide-react';
import AppFooter from './AppFooter';
import IpNoticeDialog from './IpNoticeDialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_4a87706c-aea0-440d-a2df-f352ed3952e5/artifacts/qdekaw6t_Screenshot%202024-07-25%20103325.png";

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, shortLabel: 'Dashboard' },
  { path: '/customers', label: 'Customers', icon: Users, shortLabel: 'Customers' },
  { path: '/suppliers', label: 'Suppliers', icon: Truck, shortLabel: 'Suppliers' },
  { path: '/invoices', label: 'Invoices (AR)', icon: FileText, shortLabel: 'Invoices' },
  { path: '/bills', label: 'Bills (AP)', icon: Receipt, shortLabel: 'Bills' },
  { path: '/other-cash', label: 'Other Cash', icon: Wallet, shortLabel: 'Other' },
  { path: '/sales-forecast', label: 'Sales Forecast', icon: TrendingUp, shortLabel: 'Forecast' },
  { path: '/scenarios', label: 'Scenarios', icon: Sliders, shortLabel: 'Scenarios' },
  { path: '/reports/aging', label: 'Aging Report', icon: FileText, shortLabel: 'Aging' },
  { path: '/import', label: 'Import', icon: Upload, shortLabel: 'Import' },
  { path: '/team', label: 'Team', icon: Users, shortLabel: 'Team' },
  { path: '/audit-log', label: 'Audit Log', icon: Settings, shortLabel: 'Audit' },
  { path: '/settings', label: 'Settings', icon: Settings, shortLabel: 'Settings' },
];

export const Layout = ({ children }) => {
  const { user, logout, activeBusiness, businesses, switchBusiness, createBusiness, updateBusiness, darkMode, toggleDarkMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [addBusinessDialogOpen, setAddBusinessDialogOpen] = useState(false);
  const [editBusinessDialogOpen, setEditBusinessDialogOpen] = useState(false);
  const [newBusinessName, setNewBusinessName] = useState('');
  const [newBusinessLogo, setNewBusinessLogo] = useState(null);
  const [editBusinessName, setEditBusinessName] = useState('');
  const [editBusinessLogo, setEditBusinessLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [xeroStatus, setXeroStatus] = useState(null);
  const fileInputRef = useRef(null);
  const editFileInputRef = useRef(null);

  // Check if we're on an admin page
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    checkAdminStatus();
    fetchXeroStatus();
  }, [user, activeBusiness]);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/check`);
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  const fetchXeroStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/xero/status`);
      setXeroStatus(data);
    } catch (e) {
      setXeroStatus(null);
    }
  };

  const formatRelativeTime = (iso) => {
    if (!iso) return 'never';
    const ms = Date.now() - new Date(iso).getTime();
    const m = Math.floor(ms / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchBusiness = async (businessId) => {
    try {
      await switchBusiness(businessId);
      toast.success('Switched business successfully');
    } catch (error) {
      toast.error('Failed to switch business');
    }
  };

  const handleFileChange = (e, isEdit = false) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 500000) {
        toast.error('Logo file must be less than 500KB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        if (isEdit) {
          setEditBusinessLogo(reader.result);
        } else {
          setNewBusinessLogo(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateBusiness = async () => {
    if (!newBusinessName.trim()) {
      toast.error('Business name is required');
      return;
    }
    setSaving(true);
    try {
      await createBusiness(newBusinessName, newBusinessLogo);
      toast.success('Business created successfully');
      setAddBusinessDialogOpen(false);
      setNewBusinessName('');
      setNewBusinessLogo(null);
    } catch (error) {
      toast.error('Failed to create business');
    } finally {
      setSaving(false);
    }
  };

  const handleEditBusiness = async () => {
    if (!editBusinessName.trim()) {
      toast.error('Business name is required');
      return;
    }
    setSaving(true);
    try {
      await updateBusiness(activeBusiness.id, {
        name: editBusinessName,
        logo_base64: editBusinessLogo
      });
      toast.success('Business updated successfully');
      setEditBusinessDialogOpen(false);
    } catch (error) {
      toast.error('Failed to update business');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = () => {
    setEditBusinessName(activeBusiness?.name || '');
    setEditBusinessLogo(activeBusiness?.logo_base64 || null);
    setEditBusinessDialogOpen(true);
  };

  const businessLogo = activeBusiness?.logo_base64 || DEFAULT_LOGO;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      {/* Intellectual-property acknowledgement on every fresh login */}
      <IpNoticeDialog />

      {/* Slim Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo and Business Name */}
            <div className="flex items-center gap-2">
              {/* Mobile hamburger */}
              {!isAdminPage && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileNavOpen((v) => !v)}
                  className="lg:hidden -ml-2"
                  data-testid="mobile-nav-toggle"
                >
                  {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              )}
              <img
                src={businessLogo}
                alt={activeBusiness?.name || 'Logo'}
                className="h-9 w-9 object-contain rounded"
              />
              <div className="hidden sm:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md px-2 py-1 transition-colors">
                      <span className="font-semibold text-slate-900 dark:text-slate-100 max-w-[200px] truncate">
                        {activeBusiness?.name || 'Select Business'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-64">
                    <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">Your Businesses</div>
                    {businesses.map((business) => (
                      <DropdownMenuItem
                        key={business.id}
                        onClick={() => handleSwitchBusiness(business.id)}
                        className={`cursor-pointer ${business.is_active ? 'bg-primary/10' : ''}`}
                        data-testid={`business-switch-${business.id}`}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {business.logo_base64 ? (
                            <img src={business.logo_base64} alt="" className="w-6 h-6 rounded object-contain" />
                          ) : (
                            <Building2 className="w-6 h-6 text-slate-400" />
                          )}
                          <div className="flex-1 min-w-0 flex flex-col">
                            <span className="truncate">{business.name}</span>
                            {business.role && business.role !== 'owner' && (
                              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                                Team · {business.role === 'admin' ? 'Admin' : business.role === 'editor' ? 'Editor' : 'Viewer'}
                              </span>
                            )}
                          </div>
                          {business.is_active && (
                            <span className="text-xs text-primary font-medium">Active</span>
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                    {businesses.length >= 2 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => handleSwitchBusiness('__consolidated__')}
                          className={`cursor-pointer ${activeBusiness?.is_consolidated ? 'bg-primary/10' : ''}`}
                          data-testid="business-switch-consolidated"
                        >
                          <div className="flex items-center gap-2 w-full">
                            <div className="w-6 h-6 rounded bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                              <Building2 className="w-3.5 h-3.5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0 flex flex-col">
                              <span className="truncate font-medium">All businesses</span>
                              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                                Consolidated · {businesses.length} businesses
                              </span>
                            </div>
                            {activeBusiness?.is_consolidated && (
                              <span className="text-xs text-primary font-medium">Active</span>
                            )}
                          </div>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setAddBusinessDialogOpen(true)} className="cursor-pointer">
                      <Plus className="w-4 h-4 mr-2" />
                      Add New Business
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openEditDialog} className="cursor-pointer" disabled={activeBusiness?.is_consolidated}>
                      <Settings className="w-4 h-4 mr-2" />
                      Edit Current Business
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-3">
              {isAdmin && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-purple-600 text-white'
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200 border border-purple-300'
                    }`
                  }
                  data-testid="nav-admin-header"
                >
                  <Shield className="w-4 h-4" />
                  <span>Admin</span>
                </NavLink>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleDarkMode}
                className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                data-testid="dark-mode-toggle"
              >
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>
              <span className="text-sm text-slate-600 hidden md:block dark:text-slate-300">
                {user?.name}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Body: Sidebar + Main */}
      <div className="flex flex-1 w-full">
        {/* Vertical Sidebar (hidden on admin pages) */}
        {!isAdminPage && (
          <>
            {/* Mobile overlay */}
            {mobileNavOpen && (
              <div
                className="fixed inset-0 z-30 bg-black/40 lg:hidden"
                onClick={() => setMobileNavOpen(false)}
              />
            )}
            <aside
              className={`${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:sticky lg:top-14 top-14 left-0 z-40 h-[calc(100vh-3.5rem)] w-56 shrink-0 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-transform duration-200 ease-out`}
              data-testid="main-navigation"
            >
              <nav className="flex flex-col gap-0.5 p-3 overflow-y-auto h-full">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                        isActive
                          ? 'bg-primary text-white'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800'
                      }`
                    }
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <item.icon className="w-4 h-4 shrink-0" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </nav>
            </aside>
          </>
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 py-6">
          <div className="max-w-7xl mx-auto animate-fadeIn">
            {children}
          </div>
        </main>
      </div>

      {/* Footer */}
      <AppFooter />

      {/* Add Business Dialog */}
      <Dialog open={addBusinessDialogOpen} onOpenChange={setAddBusinessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Add New Business
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="businessName">Business Name</Label>
              <Input
                id="businessName"
                value={newBusinessName}
                onChange={(e) => setNewBusinessName(e.target.value)}
                placeholder="Enter business name"
                data-testid="new-business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Business Logo (optional)</Label>
              <div className="flex items-center gap-4">
                {newBusinessLogo ? (
                  <img src={newBusinessLogo} alt="Preview" className="w-16 h-16 object-contain rounded border" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 rounded border flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileChange(e, false)}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload Logo
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">Max 500KB, PNG or JPG</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleCreateBusiness} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Business
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Business Dialog */}
      <Dialog open={editBusinessDialogOpen} onOpenChange={setEditBusinessDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Edit Business
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="editBusinessName">Business Name</Label>
              <Input
                id="editBusinessName"
                value={editBusinessName}
                onChange={(e) => setEditBusinessName(e.target.value)}
                placeholder="Enter business name"
                data-testid="edit-business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Business Logo</Label>
              <div className="flex items-center gap-4">
                {editBusinessLogo ? (
                  <img src={editBusinessLogo} alt="Preview" className="w-16 h-16 object-contain rounded border" />
                ) : (
                  <div className="w-16 h-16 bg-slate-100 rounded border flex items-center justify-center">
                    <ImageIcon className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                <div>
                  <input
                    type="file"
                    ref={editFileInputRef}
                    onChange={(e) => handleFileChange(e, true)}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => editFileInputRef.current?.click()}
                  >
                    Change Logo
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">Max 500KB, PNG or JPG</p>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleEditBusiness} disabled={saving} className="bg-primary hover:bg-primary/90">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
